import React from "react";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { alpha } from "@kaltura/mediaspace-shared-styled";
import styled from "@emotion/styled";
import clsx from "clsx";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { VisibleFocusStyle } from "@kaltura/ds-react-theme";

interface Props {
    currentItemIndex: number;
    onLineClick: (args: { index: number }) => void;
    currentTime: number;
    className?: string;
    entryCarouselInterval: number;
    itemsNames?: string[];
}

const Line = styled("button")(({ theme }) => ({
    position: "relative",
    width: "100%",
    borderRadius: 2,
    margin: 0,
    padding: "4px 5px",
    background: "transparent",
    border: 0,
    outline: 0,
    cursor: "pointer",
    "&:hover, &:active, &:focus": {
        [InnerLine as never]: {
            backgroundColor: alpha(theme.palette.common.white, 0.5),
        },
    },
    "&:focus-visible": {
        ...VisibleFocusStyle,
    },
}));
const InnerLine = styled("div")({
    position: "relative",
    height: 4,
    borderRadius: 2,
    background: "rgba(255, 255, 255, 0.3)",
});

const Container = styled("div")<{ itemsCount: number }>(({ theme, itemsCount }) => ({
    position: "absolute",
    bottom: theme.spacing(2),
    left: 0,
    right: "0",
    minWidth: 220,
    width: 55 * itemsCount,
    maxWidth: "90%",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
}));

const Progress = styled("div")(({ theme }) => ({
    height: "100%",
    borderRadius: "inherit",
    background: theme.palette.common.white,
    transformOrigin: 0,
    // transition: "transform 500ms linear",
    transition: theme.transitions.create(
        ["transform"],
        {
            easing: "linear",
            duration: theme.transitions.duration.standard,
        }
    ),
}));

export const Lines = ({
    currentItemIndex,
    className,
    entryCarouselInterval,
    currentTime,
    onLineClick,
    itemsNames = [],
}: Props) => {
    const sendButtonAnalytics = useButtonAnalytics();

    if (itemsNames.length <= 1) {
        return;
    }

    return (
        <Container className={clsx("chromatic-ignore", className)} itemsCount={itemsNames.length}>
            {itemsNames.map((name, index) => {
                const isCurrent = index === currentItemIndex;
                return (
                    <Line
                        key={index}
                        type={"button"}
                        aria-label={translate("go to %1", [name])}
                        onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            sendButtonAnalytics("Hero - Toggle Media", ButtonClickAnalyticsType.BROWSE);
                            onLineClick({ index });
                        }}
                    >
                        <InnerLine className={"hero-unit-component__lines__line__inner"}>
                            {isCurrent && (
                                <Progress
                                    className={clsx("chromatic-ignore", "hero-unit-component__lines__line__progress")}
                                    style={{
                                        transform: `scaleX(${Math.min(currentTime / entryCarouselInterval, 1)})`,
                                    }}
                                    aria-hidden={"true"}
                                />
                            )}
                        </InnerLine>
                    </Line>
                );
            })}
        </Container>
    );
};
